import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Check, X, Download, Sparkles, Calendar, ArrowRight, Info, Percent } from 'lucide-react';
import { useTheme } from '../lib/theme';
import { getTaxTimeline, getTaxReadyStatus, getTaxAccuracy } from '../lib/api';

function ChecklistItem({ ok, label, meta, colors }) {
  return (
    <div className="flex items-center gap-3 py-3" data-testid={`checklist-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: ok ? colors.successSoft : colors.warningSoft }}>
        {ok
          ? <Check size={14} style={{ color: colors.success }} />
          : <X size={14} style={{ color: colors.warning }} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold" style={{ color: colors.text }}>{label}</p>
        <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>{meta}</p>
      </div>
    </div>
  );
}

export default function TaxReady() {
  const { colors } = useTheme();
  const [timeline, setTimeline] = useState(null);
  const [status, setStatus] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s, a] = await Promise.all([
        getTaxTimeline().catch(() => null),
        getTaxReadyStatus().catch(() => null),
        getTaxAccuracy().catch(() => null),
      ]);
      setTimeline(t);
      setStatus(s);
      setAccuracy(a);
    } catch { /* silent */ }
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const ready = !!status?.ready_for_submission;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tax-ready-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight" style={{ color: colors.text }}>Tax Ready</h1>
        <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
          Track upcoming deadlines and confirm your books are ready for submission.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 rounded-full animate-spin"
            style={{ borderColor: colors.border, borderTopColor: colors.accent }} />
        </div>
      ) : (
        <>
          {/* Tax Timeline */}
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] mb-3" style={{ color: colors.textMuted }}>
            Tax Timeline
          </p>
          {timeline && timeline.settings_configured ? (
            <TimelineViz timeline={timeline} colors={colors} />
          ) : (
            <div className="rounded-2xl border p-8 text-center mb-8"
              data-testid="timeline-empty"
              style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
              <Calendar size={30} style={{ color: colors.textMuted, margin: '0 auto 8px' }} />
              <p className="text-base font-semibold" style={{ color: colors.text }}>Configure your tax settings to see upcoming deadlines</p>
              <Link to="/app/settings" className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-lg text-sm font-bold border"
                data-testid="go-to-settings"
                style={{ color: colors.accent, borderColor: colors.accent, backgroundColor: colors.surface }}>
                <ArrowRight size={13} /> Open Settings
              </Link>
            </div>
          )}

          {/* Tax Accuracy — Phase 17 */}
          {accuracy ? (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-2 mt-2" style={{ color: colors.textMuted }}>
                Tax Accuracy
              </p>
              <div
                data-testid="tax-accuracy-card"
                className="rounded-xl border p-5 mb-8 flex flex-col sm:flex-row items-center gap-6"
                style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
                <AccuracyRing pct={accuracy.accuracy_pct} colors={colors} />
                <div className="flex-1 grid grid-cols-3 gap-4 w-full">
                  <AccuracyStat label="Correct" value={accuracy.correct} tone="good" colors={colors} />
                  <AccuracyStat
                    label="Discrepancies"
                    value={accuracy.discrepancies}
                    subValue={`${accuracy.unresolved_discrepancies} unresolved`}
                    tone={accuracy.unresolved_discrepancies > 0 ? 'bad' : 'mid'} colors={colors} />
                  <AccuracyStat
                    label="Zero-rated / Exempt"
                    value={accuracy.zero_rated_or_exempt}
                    tone="neutral" colors={colors} />
                </div>
              </div>
            </>
          ) : null}

          {/* Tax Ready Checklist */}
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: colors.textMuted }}>
            Tax Ready Checklist
          </p>
          {status ? (
            <div className="rounded-xl border overflow-hidden mb-8"
              data-testid="tax-checklist"
              style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
              <div className="px-5 py-3 flex items-center justify-between border-b" style={{ borderColor: colors.border }}>
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                  Overall status
                </span>
                <span
                  data-testid="ready-status-pill"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold"
                  style={{
                    backgroundColor: ready ? colors.successSoft : colors.warningSoft,
                    color: ready ? colors.success : colors.warning,
                  }}>
                  {ready ? 'Ready for submission' : 'Not ready yet'}
                </span>
              </div>
              <div className="px-5 py-2 divide-y" style={{ borderColor: colors.border }}>
                <ChecklistItem
                  ok={status.score >= 90}
                  label="Clasifi Score is 90 or higher"
                  meta={`Currently ${status.score}/100`}
                  colors={colors} />
                <ChecklistItem
                  ok={status.unresolved_issues.high === 0}
                  label="No high-priority issues remaining"
                  meta={`${status.unresolved_issues.high} high · ${status.unresolved_issues.medium} medium`}
                  colors={colors} />
                <ChecklistItem
                  ok={status.reviewed_progress.complete}
                  label="All receipts reviewed"
                  meta={`${status.reviewed_progress.reviewed} / ${status.reviewed_progress.total} (${status.reviewed_progress.percentage.toFixed(1)}%)`}
                  colors={colors} />
              </div>
              {ready ? (
                <div className="px-5 py-4 border-t flex items-center gap-3"
                  data-testid="ready-hero"
                  style={{ borderColor: colors.border, backgroundColor: colors.successSoft }}>
                  <Sparkles size={20} style={{ color: colors.success }} />
                  <div>
                    <p className="text-sm font-bold" style={{ color: colors.text }}>
                      Your accountant will love you.
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                      {status.readiness_message}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="px-5 py-3 border-t text-xs" style={{ borderColor: colors.border, color: colors.textMuted }}>
                  {status.readiness_message}
                </div>
              )}
            </div>
          ) : null}

          {/* Send to QuickBooks */}
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: colors.textMuted }}>
            Submit
          </p>
          <div className="rounded-xl border p-5"
            style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
            <a
              href="/api/receipts/export.csv?status=approved"
              data-testid="send-to-quickbooks-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: colors.accent }}>
              <Download size={14} /> Send to QuickBooks (CSV)
            </a>
            <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
              This CSV file can be shared directly with your accountant. QuickBooks Online compatible.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- Timeline visualization ----------------
function dotColorFor(days, colors) {
  if (days <= 30) return colors.warning;
  if (days <= 90) return colors.accent;
  return colors.textMuted;
}

// ---------------- Tax Accuracy Ring ----------------
function AccuracyRing({ pct, colors }) {
  const size = 130;
  const stroke = 11;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const offset = circumference * (1 - clamped / 100);

  // Threshold tones: ≥95 green, 80–94 amber, <80 red.
  const strokeColor = clamped >= 95
    ? (colors.success || '#10B981')
    : clamped >= 80
      ? (colors.warning || '#F59E0B')
      : (colors.error || '#EF4444');
  const trackColor = colors.border || 'rgba(255,255,255,0.10)';

  const [drawn, setDrawn] = React.useState(circumference);
  React.useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(offset));
    return () => cancelAnimationFrame(id);
  }, [offset]);

  return (
    <div
      data-testid="accuracy-ring"
      style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={strokeColor} strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={drawn}
          style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(0.4, 0, 0.2, 1)' }} />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <span
          data-testid="accuracy-pct"
          className="tabular-nums"
          style={{ fontSize: 26, fontWeight: 800, color: strokeColor, lineHeight: 1 }}>
          {clamped.toFixed(1)}%
        </span>
        <span className="uppercase" style={{
          fontSize: 9, fontWeight: 700, color: colors.textMuted, letterSpacing: 0.6, marginTop: 4,
        }}>
          Accuracy
        </span>
      </div>
    </div>
  );
}

// ---------------- Tax Accuracy Stat ----------------
function AccuracyStat({ label, value, subValue, tone = 'neutral', icon, colors }) {
  const toneMap = {
    good: colors.success || '#10B981',
    mid: colors.warning || '#F59E0B',
    bad: colors.error || '#EF4444',
    neutral: colors.textMuted || '#6B7280',
  };
  const c = toneMap[tone];
  return (
    <div data-testid={`accuracy-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: colors.textMuted }}>
        {icon}
        {label}
      </p>
      <p className="text-2xl font-bold tabular-nums mt-1" style={{ color: c }}>{value}</p>
      {subValue ? (
        <p className="text-[10px] mt-0.5" style={{ color: colors.textMuted }}>{subValue}</p>
      ) : null}
    </div>
  );
}

// ---------------- Info popover (portal + glass) ----------------
function InfoPopover({ info, title, colors, open, onClose }) {
  const cardRef = React.useRef(null);
  // Escape-key + click-outside dismiss
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    // Delay attaching the mousedown listener so the same click that opened
    // the popup doesn't immediately close it.
    let attached = false;
    const attachTimer = setTimeout(() => {
      document.addEventListener('mousedown', onDocMouseDown);
      attached = true;
    }, 0);
    const onDocMouseDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(attachTimer);
      window.removeEventListener('keydown', onKey);
      if (attached) document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [open, onClose]);

  if (!open || !info) return null;
  // Detect dark mode from theme surface (light theme has near-white surface)
  const isDark = (colors.surface || '#ffffff').toLowerCase() < '#888888';
  const glassBg = isDark ? 'rgba(20,24,22,0.72)' : 'rgba(255,255,255,0.82)';
  const overlay = createPortal(
    <div
      data-testid="deadline-info-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(6,10,8,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}>
      <div
        ref={cardRef}
        data-testid="deadline-info-popover"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative', width: 'min(420px, 100%)',
          background: glassBg,
          backdropFilter: 'blur(24px) saturate(140%)',
          WebkitBackdropFilter: 'blur(24px) saturate(140%)',
          border: `1px solid ${colors.accent}55`,
          borderRadius: 20, padding: 22,
          boxShadow: `0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px ${colors.accent}22, inset 0 1px 0 rgba(255,255,255,0.15)`,
        }}>
        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em]"
              style={{ color: colors.accent }}>About this deadline</p>
            {title ? (
              <p className="text-base font-bold mt-1" style={{ color: colors.text, letterSpacing: '-0.01em' }}>
                {title}
              </p>
            ) : null}
          </div>
          <button onClick={onClose}
            style={{ color: colors.textMuted, padding: 4 }}
            className="hover:opacity-70">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] mb-1" style={{ color: colors.accent }}>What</p>
            <p className="text-sm leading-relaxed" style={{ color: colors.text }}>{info.what}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] mb-1" style={{ color: colors.accent }}>Who</p>
            <p className="text-sm leading-relaxed" style={{ color: colors.text }}>{info.who}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] mb-1" style={{ color: colors.warning }}>If missed</p>
            <p className="text-sm leading-relaxed" style={{ color: colors.text }}>{info.if_missed}</p>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
  return overlay;
}

// ---------------- Horizontal Cylindrical Timeline ----------------
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function daysInMonth(y, m /* 0-indexed */) {
  return new Date(y, m + 1, 0).getDate();
}

// Position a date within a 13-month window as a fractional index in [0, 13].
function bandOffsetFor(dateStr, startYear, startMonth) {
  const [ys, ms, ds] = dateStr.split('-').map(Number);
  const monthDelta = (ys - startYear) * 12 + (ms - 1 - startMonth);
  const dayFraction = (ds - 1) / daysInMonth(ys, ms - 1);
  return monthDelta + dayFraction;
}

function TimelineViz({ timeline, colors }) {
  const deadlines = timeline.deadlines || [];
  const [openInfo, setOpenInfo] = React.useState(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!containerRef.current) return undefined;
    const el = containerRef.current;
    const update = () => setContainerWidth(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const now = React.useMemo(() => new Date(), []);
  const startYear = now.getFullYear();
  const startMonth = now.getMonth();
  const MONTHS = 13;

  // Layout: axis runs from PAD to (containerWidth - PAD)
  const PAD = 32;
  const axisWidth = Math.max(0, containerWidth - PAD * 2);
  const BAND = axisWidth / MONTHS;
  const AXIS_HEIGHT = 12;           // cylinder thickness
  const AXIS_Y = 90;                // vertical center
  const AXIS_TOP = AXIS_Y - AXIS_HEIGHT / 2;
  const CANVAS_H = 220;

  const todayFrac = (now.getDate() - 1) / daysInMonth(startYear, startMonth);
  const todayX = PAD + todayFrac * BAND;

  // Milestone positions + alternating (above/below) staggering
  const milestones = React.useMemo(() => {
    if (!axisWidth) return [];
    const items = deadlines.map((d, idx) => {
      const off = bandOffsetFor(d.due_date, startYear, startMonth);
      const clamped = Math.max(0, Math.min(MONTHS - 0.02, off));
      const x = PAD + clamped * BAND;
      return { idx, d, x };
    }).sort((a, b) => a.x - b.x);
    // Even sorted-order → above, odd → below. Flip if same-side neighbour <130px away.
    const lastX = { above: -Infinity, below: -Infinity };
    return items.map((it, order) => {
      let side = order % 2 === 0 ? 'above' : 'below';
      if (it.x - lastX[side] < 130) side = side === 'above' ? 'below' : 'above';
      lastX[side] = it.x;
      return { ...it, side };
    });
  }, [deadlines, axisWidth, BAND, startYear, startMonth]);

  return (
    <div className="rounded-2xl border p-6 mb-4 relative overflow-hidden"
      data-testid="tax-timeline"
      style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(60% 40% at 20% 50%, ${colors.accent}18 0%, transparent 70%)`,
          opacity: 0.9,
        }} />
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] mb-4 relative z-10"
        style={{ color: colors.textMuted }}>
        Next 13 months
      </p>

      <div ref={containerRef} className="relative w-full z-10" style={{ height: CANVAS_H }}>
        {containerWidth > 0 ? (
          <svg
            width={containerWidth}
            height={CANVAS_H}
            style={{ position: 'absolute', inset: 0 }}>
            <defs>
              {/* Length-wise green gradient (left → right) */}
              <linearGradient id="cyl-length" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#0FC862" stopOpacity="1" />
                <stop offset="50%" stopColor="#00E5A0" stopOpacity="0.95" />
                <stop offset="100%" stopColor="#14F195" stopOpacity="0.75" />
              </linearGradient>
              {/* Perpendicular gloss (top light, bottom dark) for 3D depth */}
              <linearGradient id="cyl-gloss" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
                <stop offset="45%" stopColor="rgba(255,255,255,0.05)" />
                <stop offset="55%" stopColor="rgba(0,0,0,0.05)" />
                <stop offset="100%" stopColor="rgba(0,0,0,0.30)" />
              </linearGradient>
              {/* Outer halo */}
              <linearGradient id="cyl-halo" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#0FC862" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#14F195" stopOpacity="0.20" />
              </linearGradient>
              <filter id="cyl-outer-glow" x="-20%" y="-100%" width="140%" height="300%">
                <feGaussianBlur stdDeviation="3" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* Outer glow behind pill */}
            <rect
              x={PAD - 4} y={AXIS_TOP - 4}
              width={axisWidth + 8} height={AXIS_HEIGHT + 8}
              rx={(AXIS_HEIGHT + 8) / 2} ry={(AXIS_HEIGHT + 8) / 2}
              fill="url(#cyl-halo)" filter="url(#cyl-outer-glow)" />

            {/* Base cylinder — length gradient */}
            <rect
              x={PAD} y={AXIS_TOP}
              width={axisWidth} height={AXIS_HEIGHT}
              rx={AXIS_HEIGHT / 2} ry={AXIS_HEIGHT / 2}
              fill="url(#cyl-length)" />
            {/* Gloss overlay for glossy pill depth */}
            <rect
              x={PAD} y={AXIS_TOP}
              width={axisWidth} height={AXIS_HEIGHT}
              rx={AXIS_HEIGHT / 2} ry={AXIS_HEIGHT / 2}
              fill="url(#cyl-gloss)" />
            {/* Thin top highlight strip */}
            <rect
              x={PAD + 4} y={AXIS_TOP + 2}
              width={axisWidth - 8} height={1.5}
              rx={1} ry={1}
              fill="rgba(255,255,255,0.55)" />

            {/* Month tick marks below the axis */}
            {Array.from({ length: MONTHS }).map((_, i) => {
              const x = PAD + i * BAND;
              return (
                <line key={i}
                  x1={x} y1={AXIS_TOP + AXIS_HEIGHT + 2}
                  x2={x} y2={AXIS_TOP + AXIS_HEIGHT + 8}
                  stroke={colors.textMuted} strokeWidth={1} opacity={0.5} />
              );
            })}
          </svg>
        ) : null}

        {/* Month labels — below tick marks */}
        {containerWidth > 0 && Array.from({ length: MONTHS }).map((_, i) => {
          const monthIdx = (startMonth + i) % 12;
          const yearOffset = Math.floor((startMonth + i) / 12);
          const isCurrent = i === 0;
          const x = PAD + i * BAND;
          return (
            <div key={i} style={{
              position: 'absolute',
              left: x, top: AXIS_TOP + AXIS_HEIGHT + 12,
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
            }}>
              <p style={{
                fontSize: 10,
                fontWeight: isCurrent ? 700 : 500,
                color: isCurrent ? colors.accent : colors.textMuted,
                letterSpacing: '0.06em',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}>
                {MONTH_SHORT[monthIdx]}{yearOffset > 0 ? ` ${(startYear + yearOffset).toString().slice(2)}` : ''}
              </p>
            </div>
          );
        })}

        {/* TODAY marker — bright accent vertical bar + label above the pill */}
        {containerWidth > 0 ? (
          <>
            <div style={{
              position: 'absolute',
              left: todayX - 2,
              top: AXIS_TOP - 4,
              width: 4,
              height: AXIS_HEIGHT + 8,
              borderRadius: 2,
              background: '#0FC862',
              boxShadow: '0 0 12px #0FC862, 0 0 4px #14F195',
              pointerEvents: 'none',
            }} />
            <div style={{
              position: 'absolute',
              left: todayX, top: AXIS_TOP - 24,
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
            }}>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]"
                style={{ color: colors.accent }}>
                Today
              </span>
            </div>
          </>
        ) : null}

        {/* Milestone dots + staggered labels */}
        {milestones.map(({ d, x, side, idx }) => {
          const dotColor = dotColorFor(d.days_until, colors);
          const soon = d.days_until <= 30;
          const isYearEnd = d.label === 'Year-End';
          const above = side === 'above';
          const labelTop = above ? AXIS_TOP - 44 : AXIS_TOP + AXIS_HEIGHT + 34;
          return (
            <div key={idx} data-testid={`timeline-milestone-${idx}`}>
              {/* Dot on axis */}
              <div style={{
                position: 'absolute',
                left: x - 8, top: AXIS_Y - 8,
                width: 16, height: 16, borderRadius: 8,
                background: dotColor,
                border: `2px solid ${colors.surface}`,
                boxShadow: soon || isYearEnd
                  ? `0 0 0 4px ${dotColor}33, 0 0 14px ${dotColor}`
                  : `0 0 10px ${dotColor}88`,
                zIndex: 2,
              }} />
              {/* Label + info */}
              <div style={{
                position: 'absolute',
                left: x, top: labelTop,
                transform: 'translateX(-50%)',
                display: 'flex', alignItems: 'center', gap: 6,
                zIndex: 3,
              }}>
                <div style={{ textAlign: 'center' }}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em] whitespace-nowrap"
                    style={{ color: colors.text }}>
                    {d.label.replace(' filing', '')}
                  </p>
                  <p className="text-[10px] tabular-nums whitespace-nowrap mt-0.5"
                    style={{ color: colors.textMuted }}>
                    {d.due_date} · in {Math.max(0, d.days_until)}d
                  </p>
                </div>
                {d.info ? (
                  <button
                    data-testid={`timeline-info-btn-${idx}`}
                    onClick={() => setOpenInfo(idx)}
                    className="p-1 rounded-full hover:opacity-70"
                    style={{ color: colors.textMuted }}>
                    <Info size={12} />
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Countdown cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6 relative z-10">
        {deadlines.slice(0, 3).map((d, i) => {
          const soon = d.days_until <= 30;
          const dotColor = dotColorFor(d.days_until, colors);
          const isYearEnd = d.label === 'Year-End';
          return (
            <div key={i}
              data-testid={`countdown-card-${i}`}
              className="relative rounded-2xl p-6 overflow-visible"
              style={{
                background: isYearEnd
                  ? 'linear-gradient(135deg, rgba(15,200,98,0.14) 0%, rgba(0,229,160,0.10) 50%, rgba(94,234,212,0.08) 100%)'
                  : 'linear-gradient(135deg, rgba(15,200,98,0.08) 0%, rgba(0,229,160,0.05) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${soon ? colors.warning : `${colors.accent}44`}`,
                borderLeftWidth: soon ? 3 : (isYearEnd ? 2 : 1),
                boxShadow: isYearEnd
                  ? `0 8px 32px -12px ${colors.accent}55, inset 0 1px 0 rgba(255,255,255,0.08)`
                  : `0 4px 24px -14px ${colors.accent}44, inset 0 1px 0 rgba(255,255,255,0.05)`,
              }}>
              {d.info ? (
                <button
                  data-testid={`info-btn-${i}`}
                  onClick={() => setOpenInfo(i)}
                  className="absolute top-3 right-3 p-1 rounded-full hover:opacity-70"
                  style={{ color: colors.textMuted }}>
                  <Info size={14} />
                </button>
              ) : null}
              <span className="absolute top-4 right-10 w-2.5 h-2.5 rounded-full"
                style={{
                  backgroundColor: dotColor,
                  boxShadow: `0 0 10px ${dotColor}99`,
                }} />
              {isYearEnd ? (
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1"
                  style={{ color: colors.accent }}>
                  Year-End Countdown
                </p>
              ) : null}
              <p className="text-5xl font-bold tabular-nums leading-none tracking-tight"
                style={{
                  color: colors.text,
                  letterSpacing: '-0.03em',
                  ...(isYearEnd ? {
                    backgroundImage: 'linear-gradient(135deg, #0FC862 0%, #00E5A0 50%, #14F195 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  } : {}),
                }}>
                {Math.max(0, d.days_until)}
              </p>
              <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
                days until {d.label}
              </p>
              <p className="text-[11px] mt-1 tabular-nums" style={{ color: colors.textSubtle || colors.textMuted }}>
                {d.due_date} · {d.period}
              </p>
            </div>
          );
        })}
      </div>

      {/* Portalled info popover — escapes any parent overflow */}
      {openInfo !== null && deadlines[openInfo] ? (
        <InfoPopover
          info={deadlines[openInfo].info}
          title={deadlines[openInfo].label}
          colors={colors}
          open
          onClose={() => setOpenInfo(null)} />
      ) : null}
    </div>
  );
}
